// Epoch 19: Lead Generator Enhancements - Multi-Select Filter State Management
// File: src/hooks/useMultiSelectFilters.ts
// Purpose: React hook for managing complex multi-dimensional filter state

import { useState, useCallback, useEffect } from 'react';
import { MultiSelectFilterState, FilterMetadata } from '../types/filters';
import { supabase } from '../supabase/client';

const DEFAULT_FILTER_STATE: MultiSelectFilterState = {
  countries: [],
  cities: [],
  jobLevels: [],
  jobFunctions: [],
  industries: [],
  tiers: ['1', '2', '3', '4', '5'], // All tiers selected by default for backward compatibility
};

export function useMultiSelectFilters() {
  const [filters, setFilters] = useState<MultiSelectFilterState>(DEFAULT_FILTER_STATE);
  const [metadata, setMetadata] = useState<FilterMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update a specific filter category with array of values
  const updateFilter = useCallback((category: keyof MultiSelectFilterState, values: string[]) => {
    setFilters(prev => ({
      ...prev,
      [category]: values
    }));
  }, []);

  // Toggle a specific option within a filter category
  const toggleFilterOption = useCallback((category: keyof MultiSelectFilterState, value: string) => {
    setFilters(prev => {
      const currentValues = prev[category] as string[];
      const isSelected = currentValues.includes(value);
      
      return {
        ...prev,
        [category]: isSelected 
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value]
      };
    });
  }, []);

  // Clear a specific filter category
  const clearFilter = useCallback((category: keyof MultiSelectFilterState) => {
    setFilters(prev => ({
      ...prev,
      [category]: category === 'tiers' ? ['1', '2', '3', '4', '5'] : [] // Reset tiers to all selected
    }));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTER_STATE);
  }, []);

  // Check if any filters are applied (different from default state)
  const hasActiveFilters = useCallback(() => {
    return filters.countries.length > 0 ||
           filters.cities.length > 0 ||
           filters.jobLevels.length > 0 ||
           filters.jobFunctions.length > 0 ||
           filters.industries.length > 0 ||
           filters.tiers.length < 5; // Less than all tiers selected
  }, [filters]);

  // Get count of applied filters
  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    count += filters.countries.length;
    count += filters.cities.length;
    count += filters.jobLevels.length;
    count += filters.jobFunctions.length;
    count += filters.industries.length;
    count += (5 - filters.tiers.length); // Count deselected tiers
    return count;
  }, [filters]);

  // Convert filters to BigQuery-compatible format
  const toBigQueryFilters = useCallback(() => {
    return {
      norm_country: filters.countries,
      norm_city: filters.cities,
      norm_job_level: filters.jobLevels,
      norm_job_function: filters.jobFunctions,
      norm_industry: filters.industries,
      tiers: filters.tiers
    };
  }, [filters]);

  // Load filter metadata from backend
  const loadFilterMetadata = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get current session from Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      const response = await fetch('https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/getFilterMetadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load filter metadata: ${response.status}`);
      }
      
      const data = await response.json();
      setMetadata(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load filter metadata';
      setError(errorMessage);
      console.error('Error loading filter metadata:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Persist filter state to session storage
  const persistFilters = useCallback(() => {
    try {
      sessionStorage.setItem('lead_generator_filters', JSON.stringify(filters));
    } catch (err) {
      console.warn('Failed to persist filters to session storage:', err);
    }
  }, [filters]);

  // Restore filter state from session storage
  const restoreFilters = useCallback(() => {
    try {
      const stored = sessionStorage.getItem('lead_generator_filters');
      if (stored) {
        const parsedFilters = JSON.parse(stored);
        // Validate the structure before applying
        if (parsedFilters && typeof parsedFilters === 'object') {
          setFilters({
            ...DEFAULT_FILTER_STATE,
            ...parsedFilters
          });
        }
      }
    } catch (err) {
      console.warn('Failed to restore filters from session storage:', err);
    }
  }, []);

  // Auto-persist filters when they change
  useEffect(() => {
    persistFilters();
  }, [persistFilters]);

  // Auto-load metadata on mount
  useEffect(() => {
    loadFilterMetadata();
    restoreFilters();
  }, [loadFilterMetadata, restoreFilters]);

  return {
    // State
    filters,
    metadata,
    loading,
    error,
    
    // Actions
    updateFilter,
    toggleFilterOption,
    clearFilter,
    clearAllFilters,
    loadFilterMetadata,
    
    // Computed values
    hasActiveFilters: hasActiveFilters(),
    activeFilterCount: getActiveFilterCount(),
    bigQueryFilters: toBigQueryFilters(),
    
    // Direct setters for external use
    setFilters,
    setMetadata,
    setLoading,
    setError
  };
}